import { EOIcon, EOModal } from '@nx/ui/eo';
import { useI18n } from '@nx/i18n';
import { contractAlertLevel } from '@nx/domain';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { partnerCreateRequested, partnersWatchRequested } from '../../store/slices/partnersSlice';
export function PartnersListPage() {
  const { t } = useI18n();
  const { eventId = '' } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const partners = useAppSelector((s) => s.partners.items);
  const [addOpen, setAddOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    dispatch(partnersWatchRequested(eventId));
  }, [dispatch, eventId]);

  const filtered = partners.filter((p) => !statusFilter || p.status === statusFilter);

  return (
    <>
      <div className="page-head">
        <div>
          <h2>{t('eo.nav.partners')}</h2>
          <p>{t('eo.partners.subtitle')}</p>
        </div>
        <div className="page-head-actions">
          <button type="button" className="btn btn-primary" onClick={() => setAddOpen(true)}>
            <EOIcon n="plus" size={15} />
            {t('eo.partners.add')}
          </button>
        </div>
      </div>

      <div className="toolbar">
        <select className="select" style={{ maxWidth: 200 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">{t('eo.partners.allStatuses')}</option>
          <option value="negotiating">{t('eo.partners.negotiating')}</option>
          <option value="confirmed">{t('eo.partners.confirmed')}</option>
          <option value="completed">{t('eo.partners.completed')}</option>
          <option value="terminated">{t('eo.partners.terminated')}</option>
        </select>
      </div>

      <div className="partners-grid">
        {filtered.map((p) => {
          const alert = contractAlertLevel(p.contractDueDate, new Date(), p.status);
          const short = p.companyName.slice(0, 2).toUpperCase();
          return (
            <div
              key={p.id}
              className="partner-card"
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/eo/events/${eventId}/partners/${p.id}`)}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`/eo/events/${eventId}/partners/${p.id}`)}
            >
              <div className="row1">
                <div className="logo">{short}</div>
                <div>
                  <strong>{p.companyName}</strong>
                  <small>{p.sponsorshipLevel}</small>
                </div>
              </div>
              <div className="row2">
                <span className={`badge ${p.status === 'confirmed' ? 'sage' : 'clay'}`}>{p.status}</span>
                {alert === 'danger' ? <EOIcon n="alert" size={16} className="danger-icon" /> : null}
                {alert === 'warn' ? <EOIcon n="alert" size={16} className="warn-icon" /> : null}
              </div>
            </div>
          );
        })}
      </div>

      {addOpen ? (
        <AddPartnerModal
          onClose={() => setAddOpen(false)}
          onSave={(input) => {
            dispatch(partnerCreateRequested({ eventId, input }));
            setAddOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

function AddPartnerModal({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (input: { companyName: string; sponsorshipLevel?: string }) => void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [level, setLevel] = useState('Gold');
  return (
    <EOModal
      open
      title={t('eo.partners.add')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => onSave({ companyName: name, sponsorshipLevel: level })}>
            {t('common.submit')}
          </button>
        </>
      }
    >
      <div className="field">
        <label>{t('eo.partners.company')}</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label>{t('eo.partners.level')}</label>
        <input className="input" value={level} onChange={(e) => setLevel(e.target.value)} />
      </div>
    </EOModal>
  );
}
